import { useEffect } from 'react'
import { DefaultValues, Resolver, useForm } from 'react-hook-form'
import { State } from '..'
import clone from 'clone-deep'

import { getDefaultPageState } from '../config'
import { usePersistentState } from '../persistentState'
import { StepPage } from 'directFlow/types'
import { useEditMode } from 'directFlow/utils'
import { FlowContext, useFlowContext } from 'directFlow/utils/FlowContext'

import { QuoteV2, TestGroup } from 'types'

type UseFormManagerParams<CurrentPage extends StepPage> = {
  resolver: Resolver<State[CurrentPage]>
  page: CurrentPage
  additionalContext?: {
    quoteV2Context?: QuoteV2
    showPreviousInsuranceCancelled?: boolean
    growth_payment_intention_question?: TestGroup
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    defaultFormValues?: any
  }
}

export const useFormManager = <CurrentPage extends StepPage>({
  resolver,
  page,
  additionalContext,
}: UseFormManagerParams<CurrentPage>) => {
  const isEditMode = useEditMode()
  const flowContext = useFlowContext()
  const persistentState = usePersistentState()

  const defaultValues: DefaultValues<State[CurrentPage]> =
    additionalContext?.defaultFormValues ?? persistentState.getPageState(page)

  const { setValue, handleSubmit, resetField, ...useFormReturnValue } = useForm({
    // On post-quote edit, errors are shown from the first render
    // so we need to update error state on change
    mode: flowContext === FlowContext.DIRECT ? 'onSubmit' : 'onChange',
    reValidateMode: 'onChange',
    defaultValues,
    context: {
      ...persistentState.stateRef.current?.pageState,
      channel: additionalContext?.quoteV2Context?.channel.name,
    },
    resolver,
  })

  useEffect(() => {
    if (isEditMode) {
      return
    }

    persistentState.onPageInit(page)
    return () => persistentState.onPageClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const enhancedSetValue: typeof setValue = (...params) => {
    const [fieldName, value] = params
    setValue(...params)

    if (!isEditMode) {
      persistentState.updateField(page, fieldName, value)
    }
  }

  const enhancedHandleSubmit: typeof handleSubmit = (onValid, onInvalid) => {
    const handleValidSubmit: Parameters<typeof handleSubmit>[0] = (formData, event) => {
      // When navigating away, field's value seems to be removed and the data received
      // (`formData`) seems to be mutated, we clone the object to preserve the form data
      // state at submit
      const data = clone(formData)

      if (isEditMode) {
        persistentState.updatePageFields(page, data as DefaultValues<State[CurrentPage]>)
      } else {
        persistentState.updateFormProgress(page)
      }

      onValid(data, event)
    }

    return handleSubmit(handleValidSubmit, onInvalid)
  }

  const enhancedResetField: typeof resetField = (...params) => {
    const [fieldName] = params
    resetField(...params)
    const defaultPageState = getDefaultPageState(page)

    // TODO: Is there a way to type this properly? It seems that the whole typing of
    // @ts-expect-error our typing for react-hook-form skips the fact that almost all values can be undefined
    persistentState.updateField(page, fieldName, defaultPageState[fieldName])
  }

  return {
    setValue: enhancedSetValue,
    handleSubmit: enhancedHandleSubmit,
    resetField: enhancedResetField,
    originalState: defaultValues,
    ...useFormReturnValue,
  }
}
